/**
 * UserDetailPanel - Airtable-style side panel for user record details
 * Shows all linked data: preference cards, events, RSVPs, orgs, pilot intakes
 * 
 * Last Updated: March 20, 2026
 */
import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DefaultAvatar } from "@/components/DefaultAvatars";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  X,
  RefreshCw,
  Save,
  Edit3,
  ExternalLink,
  Copy,
  ChevronRight,
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Building2,
  Briefcase,
  Utensils,
  Accessibility,
  Heart,
  Scissors,
  Sparkles,
  Star,
  Link2,
  Tag,
  Globe,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Ticket,
  MessageSquare,
  Users,
  History,
  Plus,
  Eye,
  EyeOff,
  Instagram,
  Linkedin,
  Twitter,
  Image,
  FileText,
  LayoutDashboard,
  UserCircle
} from "lucide-react";
import { AttendeePreferenceCardModal } from "@/components/AttendeePreferenceCard";
import LinkHostDashboardWizard from "./LinkHostDashboardWizard";
import HostAccessControlCenter from "./HostAccessControlCenter";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

async function openViewAsUser(userId, target = "/me") {
  try {
    const res = await fetch(`${API}/admin/view-as`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, target }),
    });
    const data = await res.json();
    const tok = data?.view_as_token || data?.token;
    let resolvedTarget = target;
    if (target === "/host/dashboard" || target === "host") {
      try {
        const u = await fetch(`${API}/admin/database/users/record/${userId}`, { credentials: "include" });
        const ud = await u.json();
        const evt = ud?.record?.primary_event_id;
        if (evt) resolvedTarget = `/host/${evt}`;
      } catch {}
    }
    if (tok) {
      window.open(`${BACKEND}/auth/view-as?token=${tok}&target=${encodeURIComponent(resolvedTarget)}`, "_blank");
    } else if (data?.magic_link) {
      window.open(`${BACKEND}${data.magic_link}`, "_blank");
    } else {
      alert("Unable to generate view-as link.");
    }
  } catch (e) {
    alert("View-as failed: " + e.message);
  }
}

// Category icons for preference display
const CATEGORY_CONFIG = {
  dietary: { icon: Utensils, color: "text-orange-500", bg: "bg-orange-50", border: "border-orange-200" },
  accessibility: { icon: Accessibility, color: "text-blue-500", bg: "bg-blue-50", border: "border-blue-200" },
  interests: { icon: Heart, color: "text-pink-500", bg: "bg-pink-50", border: "border-pink-200" },
  hair: { icon: Scissors, color: "text-purple-500", bg: "bg-purple-50", border: "border-purple-200" },
  skin: { icon: Sparkles, color: "text-rose-500", bg: "bg-rose-50", border: "border-rose-200" },
  audience: { icon: Users, color: "text-cyan-500", bg: "bg-cyan-50", border: "border-cyan-200" }
};

export default function UserDetailPanel({ 
  userId, 
  onClose, 
  onUpdate,
  showSensitiveData = false 
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditing, setIsEditing] = useState(false);
  
  // Core data
  const [user, setUser] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [innerCircleMemberships, setInnerCircleMemberships] = useState([]);
  const [pilotIntakes, setPilotIntakes] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [hostingPilots, setHostingPilots] = useState([]); // Pilots user is hosting
  const [inspirations, setInspirations] = useState([]); // User saved inspo images
  
  // Edit form
  const [editForm, setEditForm] = useState({});

  const token = localStorage.getItem("session_token");
  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  });

  // All available tags for autocomplete
  const [availableTags, setAvailableTags] = useState([]);
  const [tagSearch, setTagSearch] = useState("");
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  
  // Role tags (multi-select) - roles are just special tags
  const [selectedRoleTags, setSelectedRoleTags] = useState([]);
  const [roleTagSearch, setRoleTagSearch] = useState("");
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  
  // Add organization
  const [showAddOrgDialog, setShowAddOrgDialog] = useState(false);
  const [orgSearch, setOrgSearch] = useState("");
  const [orgSearchResults, setOrgSearchResults] = useState([]);
  const [searchingOrgs, setSearchingOrgs] = useState(false);
  
  // Preference card viewer
  const [viewingPreferenceCard, setViewingPreferenceCard] = useState(null);
  const [shareCards, setShareCards] = useState([]);
  const [linkedEvents, setLinkedEvents] = useState([]);
  
  // Available role tags
  const ROLE_TAGS = [
    "host", "instructor", "speaker", "vip", "cohost", "provider",
    "studio_owner", "admin", "super_admin", "customer"
  ];

  // Deprecated/irrelevant tags to filter out
  const DEPRECATED_TAGS = [
    "invite_to_create_profile", "needs_profile", "pending_invite",
    "test_tag", "deprecated", "old_tag", "temp"
  ];

  // Fetch all available tags once
  const fetchAvailableTags = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/tags`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        // Filter out deprecated tags
        const filtered = (data.tags || []).filter(t => !DEPRECATED_TAGS.includes(t));
        setAvailableTags(filtered);
      }
    } catch (err) {
      console.log("Tags fetch skipped:", err.message);
    }
  }, []);

  // Fetch all user data from comprehensive endpoint
  const fetchUserData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    
    try {
      // Fetch user profile with ALL linked data from comprehensive endpoint
      const res = await fetch(`${API}/admin/profiles/${userId}`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        
        // Set all data directly from the comprehensive response
        setUser(data.profile);
        setDefaults(data.defaults);
        setRsvps(data.rsvps || []);
        setExperiences(data.experiences || []);
        setInspirations(data.inspirations || []);
        setOrgs(data.organizations || []);
        setPilotIntakes(data.pilot_intakes || []);
        setInnerCircleMemberships(data.inner_circle_memberships || []);
        setShareCards(data.share_cards || []);
        setLinkedEvents(data.linked_events || []);
        
        // Extract role tags from user tags (filter to only role-related tags)
        const userTags = (data.profile?.tags || []).filter(t => !DEPRECATED_TAGS.includes(t));
        const roleTags = userTags.filter(t => ROLE_TAGS.includes(t));
        const nonRoleTags = userTags.filter(t => !ROLE_TAGS.includes(t));
        
        // Also consider the legacy "role" field as a role tag
        const legacyRole = data.profile?.role;
        if (legacyRole && legacyRole !== "customer" && !roleTags.includes(legacyRole)) {
          roleTags.push(legacyRole);
        }
        
        setSelectedRoleTags(roleTags);
        setSelectedTags(nonRoleTags);
        
        setEditForm({
          name: data.profile?.name || "",
          email: data.profile?.email || "",
          phone: data.profile?.phone || "",
          company: data.profile?.company || "",
          title: data.profile?.title || "",
          bio: data.profile?.bio || "",
          instagram: data.profile?.instagram || "",
          linkedin: data.profile?.linkedin || "",
          tiktok: data.profile?.tiktok || "",
          website: data.profile?.website || "",
          location: data.profile?.location || "",
          role: data.profile?.role || "customer",
          role_title: data.profile?.role_title || "",
          notes: data.profile?.notes || "",
          tags: userTags.join(", "),
          organizations: data.organizations || []
        });
        
        // Fetch hosting pilots for this user
        try {
          const hostingRes = await fetch(`${API}/me/hosting?user_id=${userId}`, { headers: getHeaders() });
          if (hostingRes.ok) {
            const hostingData = await hostingRes.json();
            setHostingPilots(hostingData.pilots || hostingData.hosting || []);
          }
        } catch (err) {
          // Try alternative endpoint for admin
          try {
            const pilotsRes = await fetch(`${API}/admin/user-pilots/${userId}`, { headers: getHeaders() });
            if (pilotsRes.ok) {
              const pilotsData = await pilotsRes.json();
              setHostingPilots(pilotsData.pilots || []);
            }
          } catch (e) {
            console.log("Hosting pilots fetch skipped");
          }
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || "Failed to load user profile");
      }
      
    } catch (err) {
      console.error("Fetch user data error:", err);
      toast.error("Failed to load user data");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUserData();
    fetchAvailableTags();
  }, [fetchUserData, fetchAvailableTags]);

  // Save user changes
  const handleSave = async () => {
    setSaving(true);
    try {
      // Combine role tags and other tags into a single tags array
      const allTags = [...new Set([...selectedRoleTags, ...selectedTags])];
      
      const updateData = {
        ...editForm,
        tags: allTags,
        // Store the primary role for backward compatibility (first role tag or 'customer')
        role: selectedRoleTags.length > 0 ? selectedRoleTags[0] : "customer",
        // Don't include organizations in update - they should be managed separately
      };
      delete updateData.organizations;
      
      const res = await fetch(`${API}/admin/users/${userId}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(updateData)
      });
      
      if (res.ok) {
        toast.success("User updated");
        setIsEditing(false);
        fetchUserData();
        onUpdate?.();
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || "Failed to update user");
      }
    } catch (err) {
      toast.error("Error updating user");
    } finally {
      setSaving(false);
    }
  };

  // Search organizations
  const searchOrganizations = async (query) => {
    if (!query || query.length < 2) {
      setOrgSearchResults([]);
      return;
    }
    setSearchingOrgs(true);
    try {
      const res = await fetch(`${API}/organizations/search?q=${encodeURIComponent(query)}`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        // Filter out already linked orgs
        const linkedOrgIds = (editForm.organizations || []).map(o => o.org_id);
        const filtered = (data.organizations || data || []).filter(o => !linkedOrgIds.includes(o.org_id));
        setOrgSearchResults(filtered);
      }
    } catch (err) {
      console.log("Org search error:", err);
    } finally {
      setSearchingOrgs(false);
    }
  };

  // Add organization to user
  const handleAddOrganization = async (org) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/organizations`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ org_id: org.org_id, role: "member" })
      });
      
      if (res.ok) {
        // Add to local state
        setEditForm(prev => ({
          ...prev,
          organizations: [...(prev.organizations || []), { ...org, user_role: "member" }]
        }));
        setOrgs(prev => [...prev, { ...org, user_role: "member" }]);
        setShowAddOrgDialog(false);
        setOrgSearch("");
        setOrgSearchResults([]);
        toast.success(`Added to ${org.name}`);
      } else {
        toast.error("Failed to add organization");
      }
    } catch (err) {
      toast.error("Error adding organization");
    }
  };

  // Remove organization from user
  const handleRemoveOrganization = async (orgId, idx) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/organizations/${orgId}`, {
        method: "DELETE",
        headers: getHeaders()
      });
      
      if (res.ok || res.status === 404) { // 404 means already removed
        setEditForm(prev => ({
          ...prev,
          organizations: prev.organizations.filter((_, i) => i !== idx)
        }));
        setOrgs(prev => prev.filter(o => o.org_id !== orgId));
        toast.success("Organization removed");
      } else {
        toast.error("Failed to remove organization");
      }
    } catch (err) {
      toast.error("Error removing organization");
    }
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  // Mask sensitive data
  const maskData = (text, type = "email") => {
    if (!text || showSensitiveData) return text;
    if (type === "email") {
      const [local, domain] = text.split("@");
      if (!domain) return text;
      return `${local.slice(0, 2)}***@${domain}`;
    }
    if (type === "phone") {
      return `***-***-${text.slice(-4)}`;
    }
    return text;
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col bg-white border-l" data-testid="user-detail-panel">
        <div className="p-4 border-b flex items-center justify-between">
          <Skeleton className="h-6 w-32" />
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 p-4 space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="h-full flex flex-col bg-white border-l">
        <div className="p-4 border-b flex items-center justify-between">
          <span className="font-medium">User Not Found</span>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center text-gray-500">
          User data not available
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white border-l shadow-lg" data-testid="user-detail-panel">
      {/* Header */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            {user.picture || user.profile_image ? (
              <Avatar className="h-14 w-14 ring-2 ring-white shadow">
                <AvatarImage src={user.picture || user.profile_image} />
                <AvatarFallback>
                  <DefaultAvatar
                    firstName={(user.name || "?").split(" ")[0]}
                    lastName={(user.name || "").split(" ").slice(1).join(" ")}
                    size={56}
                  />
                </AvatarFallback>
              </Avatar>
            ) : (
              <DefaultAvatar
                firstName={(user.name || "?").split(" ")[0]}
                lastName={(user.name || "").split(" ").slice(1).join(" ")}
                size={56}
                className="ring-2 ring-white shadow"
              />
            )}
            <div>
              <h2 className="font-semibold text-lg">{user.name || "Unnamed User"}</h2>
              <p className="text-sm text-gray-500">{maskData(user.email, "email")}</p>
              {user.company && (
                <p className="text-xs text-gray-400">{user.company}</p>
              )}
              <div className="flex items-center gap-1.5 mt-1.5">
                <button
                  onClick={() => openViewAsUser(userId, "/me")}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-black text-white hover:bg-gray-800 flex items-center gap-1"
                  data-testid={`detail-open-me-${userId}`}
                >
                  <UserCircle className="w-2.5 h-2.5" /> /me
                </button>
                <button
                  onClick={() => openViewAsUser(userId, "/host/dashboard")}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-white text-black border border-black hover:bg-gray-100 flex items-center gap-1"
                  data-testid={`detail-open-host-${userId}`}
                >
                  <LayoutDashboard className="w-2.5 h-2.5" /> Host Dashboard
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant={isEditing ? "default" : "outline"} size="sm" onClick={() => setIsEditing(!isEditing)} data-testid="toggle-edit-btn">
              <Edit3 className="w-3 h-3 mr-1" />{isEditing ? "Editing" : "Edit"}
            </Button>
            <Button variant="ghost" size="icon" onClick={fetchUserData} title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Quick badges */}
        <div className="flex flex-wrap gap-1.5">
          {user.role && user.role !== "customer" && (
            <Badge variant="outline" className="text-xs capitalize">{user.role}</Badge>
          )}
          {user.is_super_admin && (
            <Badge className="bg-purple-100 text-purple-700 text-xs">
              <Shield className="w-3 h-3 mr-1" />Admin
            </Badge>
          )}
          {defaults && (
            <Badge className="bg-green-100 text-green-700 text-xs">
              <CheckCircle className="w-3 h-3 mr-1" />Has Preferences
            </Badge>
          )}
          {user.beehiiv_subscriber_id && (
            <Badge className="bg-orange-100 text-orange-700 text-xs">
              <Globe className="w-3 h-3 mr-1" />Beehiiv
            </Badge>
          )}
          {user.plan === "legacy_pilot_free" && (
            <Badge className="bg-amber-100 text-amber-700 text-xs">
              <Sparkles className="w-3 h-3 mr-1" />Legacy Pilot
            </Badge>
          )}
        </div>

        {/* Host Access Control Center (admin one-stop shop) */}
        {(user.role === "host" || user.is_pilot || user.is_host) && (
          <HostAccessControlCenter
            user={user}
            organizations={orgs}
            linkedEvents={linkedEvents}
            pilotIntakes={pilotIntakes}
            onRefresh={fetchUserData}
          />
        )}

        {/* Link Host Dashboard slide-down (admin-only - legacy wizard) */}
        {(user.role === "host" || user.is_pilot) && (
          <LinkHostDashboardWizard user={user} />
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
        <TabsList className="px-4 pt-2 justify-start border-b bg-transparent h-auto flex-wrap gap-0.5">
          <TabsTrigger value="overview" className="text-xs" data-testid="user-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="preferences" className="text-xs" data-testid="user-tab-preferences">Defaults + Inspo</TabsTrigger>
          <TabsTrigger value="events" className="text-xs" data-testid="user-tab-events">
            Experiences {rsvps.length > 0 && <Badge variant="secondary" className="ml-1 h-4 text-[10px]">{rsvps.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="linked" className="text-xs" data-testid="user-tab-linked">Linked</TabsTrigger>
          <TabsTrigger value="edit" className="text-xs" data-testid="user-tab-edit">Edit</TabsTrigger>
        </TabsList>

        <ScrollArea className="flex-1">
          {/* Overview Tab - Comprehensive synced view */}
          <TabsContent value="overview" className="p-4 space-y-4 m-0">
            {/* Profile Info - synced from what user fills out */}
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Profile</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span className={`truncate ${!showSensitiveData ? "blur-sm" : ""}`}>{user.email || "-"}</span>
                </div>
                {user.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span className={!showSensitiveData ? "blur-sm" : ""}>{user.phone}</span>
                  </div>
                )}
                {user.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span>{user.location}</span>
                  </div>
                )}
                {user.company && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span>{user.company}</span>
                  </div>
                )}
                {user.title && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span>{user.title}</span>
                  </div>
                )}
                {user.role_title && (
                  <div className="flex items-center gap-2">
                    <Shield className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span className="capitalize">{user.role_title.replace(/_/g, " ")}</span>
                  </div>
                )}
              </div>
              {user.bio && (
                <p className="text-xs text-gray-600 italic mt-1">{user.bio}</p>
              )}
            </div>

            <Separator />

            {/* Social - synced from profile */}
            {(user.instagram || user.tiktok || user.linkedin || user.website) && (
              <>
                <div className="space-y-2">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Social</h3>
                  <div className="flex flex-wrap gap-3">
                    {user.instagram && (
                      <a href={`https://instagram.com/${user.instagram.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-pink-600 hover:underline">
                        <Instagram className="w-3.5 h-3.5" />@{user.instagram.replace("@", "")}
                      </a>
                    )}
                    {user.tiktok && (
                      <span className="flex items-center gap-1 text-xs text-gray-700">
                        TikTok: @{user.tiktok.replace("@", "")}
                      </span>
                    )}
                    {user.linkedin && (
                      <a href={user.linkedin.startsWith("http") ? user.linkedin : `https://linkedin.com/in/${user.linkedin}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                        <Linkedin className="w-3.5 h-3.5" />LinkedIn
                      </a>
                    )}
                    {user.website && (
                      <a href={user.website.startsWith("http") ? user.website : `https://${user.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-purple-600 hover:underline">
                        <Globe className="w-3.5 h-3.5" />Website
                      </a>
                    )}
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* Roles */}
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Roles</h3>
              <div className="flex flex-wrap gap-1.5">
                {selectedRoleTags.length > 0 ? (
                  selectedRoleTags.map((role, idx) => (
                    <Badge key={idx} className="text-xs bg-purple-100 text-purple-700 capitalize">{role.replace(/_/g, " ")}</Badge>
                  ))
                ) : (
                  <Badge variant="secondary" className="text-xs">customer</Badge>
                )}
              </div>
            </div>

            <Separator />

            {/* Quick Stats */}
            <div className="grid grid-cols-5 gap-2">
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold text-purple-600">{rsvps.length}</p>
                <p className="text-[10px] text-gray-500">RSVPs</p>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold text-blue-600">{experiences.length}</p>
                <p className="text-[10px] text-gray-500">Experiences</p>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold text-green-600">{orgs.length}</p>
                <p className="text-[10px] text-gray-500">Orgs</p>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold text-violet-600">{shareCards.length}</p>
                <p className="text-[10px] text-gray-500">Cards</p>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <p className="text-lg font-bold text-amber-600">{linkedEvents.length}</p>
                <p className="text-[10px] text-gray-500">Events</p>
              </div>
            </div>

            <Separator />

            {/* Experiences Summary */}
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Experiences</h3>
              {rsvps.length > 0 ? (
                <div className="space-y-1.5">
                  {rsvps.slice(0, 5).map((rsvp, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        <Calendar className="w-3 h-3 text-gray-400 shrink-0" />
                        <span className="truncate font-medium">{rsvp.event_name || rsvp.event_id}</span>
                      </div>
                      <Badge variant={rsvp.status === "confirmed" ? "default" : "secondary"} className="text-[10px] shrink-0 ml-2">
                        {rsvp.status}
                      </Badge>
                    </div>
                  ))}
                  {rsvps.length > 5 && (
                    <button onClick={() => setActiveTab("events")} className="text-xs text-blue-600 hover:underline">
                      +{rsvps.length - 5} more experiences
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No experiences yet</p>
              )}
            </div>

            <Separator />

            {/* Defaults Summary - Show actual details */}
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Defaults & Preferences</h3>
              {defaults ? (
                <div className="space-y-2">
                  {defaults.dietary && (
                    <div className="p-2 bg-orange-50 rounded-lg border border-orange-100">
                      <p className="text-[10px] font-semibold text-orange-700 uppercase mb-1">Dietary</p>
                      {defaults.dietary.dietary_restrictions?.length > 0 && (
                        <p className="text-xs text-gray-600">{defaults.dietary.dietary_restrictions.join(", ")}</p>
                      )}
                      {defaults.dietary.allergies?.length > 0 && (
                        <p className="text-xs text-gray-600">Allergies: {defaults.dietary.allergies.join(", ")}</p>
                      )}
                    </div>
                  )}
                  {defaults.accessibility && (
                    <div className="p-2 bg-blue-50 rounded-lg border border-blue-100">
                      <p className="text-[10px] font-semibold text-blue-700 uppercase mb-1">Accessibility</p>
                      {(defaults.accessibility.physical_needs || defaults.accessibility.needs)?.length > 0 && (
                        <p className="text-xs text-gray-600">{(defaults.accessibility.physical_needs || defaults.accessibility.needs).join(", ")}</p>
                      )}
                    </div>
                  )}
                  {defaults.interests?.length > 0 && (
                    <div className="p-2 bg-pink-50 rounded-lg border border-pink-100">
                      <p className="text-[10px] font-semibold text-pink-700 uppercase mb-1">Interests ({defaults.interests.length})</p>
                      <div className="flex flex-wrap gap-1">
                        {defaults.interests.map((int, i) => (
                          <Badge key={i} variant="outline" className="text-[10px] bg-white">{typeof int === 'string' ? int : int.name || int.label || JSON.stringify(int)}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {defaults.hair && (
                    <div className="p-2 bg-purple-50 rounded-lg border border-purple-100">
                      <p className="text-[10px] font-semibold text-purple-700 uppercase mb-1">Hair</p>
                      <p className="text-xs text-gray-600">
                        {[defaults.hair.type, defaults.hair.texture, defaults.hair.length].filter(Boolean).join(" | ") || "Saved"}
                      </p>
                    </div>
                  )}
                  {defaults.skin && (
                    <div className="p-2 bg-rose-50 rounded-lg border border-rose-100">
                      <p className="text-[10px] font-semibold text-rose-700 uppercase mb-1">Skin</p>
                      <p className="text-xs text-gray-600">
                        {[defaults.skin.type, defaults.skin.tone, defaults.skin.concerns].flat().filter(Boolean).join(" | ") || "Saved"}
                      </p>
                    </div>
                  )}
                  {!defaults.dietary && !defaults.accessibility && !defaults.hair && !defaults.skin && !(defaults.interests?.length > 0) && (
                    <p className="text-xs text-gray-400">Defaults saved but no categories filled</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No preferences saved</p>
              )}
              {inspirations.length > 0 && (
                <p className="text-xs text-gray-500">{inspirations.length} inspo images saved</p>
              )}
            </div>

            <Separator />

            {/* Linked Orgs Summary */}
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Hosts</h3>
              {orgs.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {orgs.map((org, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      <Building2 className="w-3 h-3 mr-1" />{org.name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No organizations</p>
              )}
            </div>

            <Separator />

            {/* Portals & Pilots */}
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pilot Portals</h3>
              {(hostingPilots.length > 0 || pilotIntakes.length > 0) ? (
                <div className="space-y-1.5">
                  {hostingPilots.map((pilot, i) => (
                    <div key={`h-${i}`} className="flex items-center justify-between p-2 bg-green-50 rounded-lg text-xs">
                      <span className="font-medium">{pilot.name || pilot.pilot_name}</span>
                      <Badge variant="outline" className="text-[10px] capitalize">{pilot.role || "member"}</Badge>
                    </div>
                  ))}
                  {pilotIntakes.map((intake, i) => (
                    <div key={`i-${i}`} className="flex items-center justify-between p-2 bg-purple-50 rounded-lg text-xs">
                      <span className="font-medium">{intake.sub_category_name || intake.pilot_type}</span>
                      <Badge variant="outline" className="text-[10px]">{intake.status || "pending"}</Badge>
                    </div>
                  ))}
                </div>
              ) : rsvps.length > 0 ? (
                <div className="space-y-1.5">
                  {[...new Set(rsvps.map(r => r.event_type || r.pilot_type || "event"))].map((type, i) => (
                    <Badge key={i} variant="outline" className="text-xs capitalize mr-1">{type}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No pilot portals</p>
              )}
            </div>

            <Separator />

            {/* Preference Cards */}
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Preference Cards</h3>
              {shareCards.length > 0 ? (
                <div className="space-y-1.5">
                  {shareCards.slice(0, 5).map((card, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-violet-50 rounded-lg text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-3 h-3 text-violet-400 shrink-0" />
                        <span className="truncate font-medium">{card.event_name || card.category || card.type || "Preference Card"}</span>
                      </div>
                      <span className="text-[10px] text-gray-400 shrink-0 ml-2">{card.created_at ? new Date(card.created_at).toLocaleDateString() : ""}</span>
                    </div>
                  ))}
                  {shareCards.length > 5 && (
                    <p className="text-xs text-gray-400">+{shareCards.length - 5} more</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No preference cards shared</p>
              )}
            </div>

            <Separator />

            {/* Linked Events */}
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Linked Events</h3>
              {linkedEvents.length > 0 ? (
                <div className="space-y-1.5">
                  {linkedEvents.slice(0, 5).map((evt, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-sky-50 rounded-lg text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        <Calendar className="w-3 h-3 text-sky-400 shrink-0" />
                        <span className="truncate font-medium">{evt.name || evt.event_id}</span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-2">
                        <Badge variant="outline" className="text-[10px] capitalize">{evt.relationship}</Badge>
                        {evt.pilot_category && (
                          <Badge variant="secondary" className="text-[10px] capitalize">{evt.pilot_category}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                  {linkedEvents.length > 5 && (
                    <p className="text-xs text-gray-400">+{linkedEvents.length - 5} more events</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No linked events</p>
              )}
            </div>

            <Separator />

            {/* Planned Experiences */}
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Planned Experiences</h3>
              {experiences.length > 0 ? (
                <div className="space-y-1.5">
                  {experiences.slice(0, 5).map((exp, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        <Sparkles className="w-3 h-3 text-amber-400 shrink-0" />
                        <span className="truncate font-medium">{exp.title || exp.category || "Experience"}</span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-2">
                        {exp.provider_name && <span className="text-[10px] text-gray-500">{exp.provider_name}</span>}
                        {exp.date && <span className="text-[10px] text-gray-400">{new Date(exp.date).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  ))}
                  {experiences.length > 5 && (
                    <p className="text-xs text-gray-400">+{experiences.length - 5} more</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No planned experiences</p>
              )}
            </div>

            <Separator />

            {/* Tags - clean, only relevant ones */}
            {selectedTags.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tags</h3>
                <div className="flex flex-wrap gap-1">
                  {selectedTags.map((tag, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Beehiiv */}
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Beehiiv</h3>
              {user.beehiiv_subscriber_id ? (
                <div className="flex items-center gap-2 text-xs">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  <span className="text-green-700">Synced</span>
                  <span className="font-mono text-gray-400 truncate">{user.beehiiv_subscriber_id}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <XCircle className="w-3.5 h-3.5" />
                  <span>Not synced</span>
                </div>
              )}
            </div>

            {/* Metadata */}
            <div className="text-xs text-gray-400 pt-2 space-y-1 border-t">
              <div className="flex justify-between">
                <span>User ID:</span>
                <span className="font-mono">{user.user_id}</span>
              </div>
              <div className="flex justify-between">
                <span>Created:</span>
                <span>{user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}</span>
              </div>
              {user.subscription_tier && (
                <div className="flex justify-between">
                  <span>Plan:</span>
                  <span className="capitalize">{user.subscription_tier}</span>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences" className="p-4 space-y-4 m-0">
            {defaults ? (
              <>
                {/* Dietary */}
                {defaults.dietary && (
                  <div className={`p-3 rounded-lg ${CATEGORY_CONFIG.dietary.bg} border ${CATEGORY_CONFIG.dietary.border}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Utensils className={`w-4 h-4 ${CATEGORY_CONFIG.dietary.color}`} />
                      <span className="font-medium text-sm">Dietary</span>
                    </div>
                    {defaults.dietary.dietary_restrictions?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {defaults.dietary.dietary_restrictions.map((r, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{r}</Badge>
                        ))}
                      </div>
                    )}
                    {defaults.dietary.allergies?.length > 0 && (
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Allergies:</span> {defaults.dietary.allergies.join(", ")}
                      </div>
                    )}
                    {defaults.dietary.meat_preferences?.length > 0 && (
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Meat:</span> {defaults.dietary.meat_preferences.join(", ")}
                      </div>
                    )}
                  </div>
                )}

                {/* Accessibility */}
                {defaults.accessibility && (
                  <div className={`p-3 rounded-lg ${CATEGORY_CONFIG.accessibility.bg} border ${CATEGORY_CONFIG.accessibility.border}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Accessibility className={`w-4 h-4 ${CATEGORY_CONFIG.accessibility.color}`} />
                      <span className="font-medium text-sm">Accessibility</span>
                    </div>
                    {(defaults.accessibility.physical_needs || defaults.accessibility.needs)?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {(defaults.accessibility.physical_needs || defaults.accessibility.needs).map((n, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{n}</Badge>
                        ))}
                      </div>
                    )}
                    {defaults.accessibility.invisible_disabilities?.length > 0 && (
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Invisible:</span> {defaults.accessibility.invisible_disabilities.join(", ")}
                      </div>
                    )}
                  </div>
                )}

                {/* Interests */}
                {defaults.interests?.length > 0 && (
                  <div className={`p-3 rounded-lg ${CATEGORY_CONFIG.interests.bg} border ${CATEGORY_CONFIG.interests.border}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className={`w-4 h-4 ${CATEGORY_CONFIG.interests.color}`} />
                      <span className="font-medium text-sm">Interests</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {defaults.interests.map((interest, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{interest}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Hair */}
                {defaults.hair && (
                  <div className={`p-3 rounded-lg ${CATEGORY_CONFIG.hair.bg} border ${CATEGORY_CONFIG.hair.border}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Scissors className={`w-4 h-4 ${CATEGORY_CONFIG.hair.color}`} />
                      <span className="font-medium text-sm">Hair</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {defaults.hair.hair_type && <div>Type: {defaults.hair.hair_type}</div>}
                      {defaults.hair.hair_texture && <div>Texture: {defaults.hair.hair_texture}</div>}
                      {defaults.hair.hair_length && <div>Length: {defaults.hair.hair_length}</div>}
                      {defaults.hair.hair_color && <div>Color: {defaults.hair.hair_color}</div>}
                    </div>
                  </div>
                )}

                {/* Skin */}
                {defaults.skin && (
                  <div className={`p-3 rounded-lg ${CATEGORY_CONFIG.skin.bg} border ${CATEGORY_CONFIG.skin.border}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className={`w-4 h-4 ${CATEGORY_CONFIG.skin.color}`} />
                      <span className="font-medium text-sm">Skin</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {defaults.skin.skin_type && <div>Type: {defaults.skin.skin_type}</div>}
                      {defaults.skin.skin_tone && <div>Tone: {defaults.skin.skin_tone}</div>}
                      {defaults.skin.skin_concerns?.length > 0 && (
                        <div className="col-span-2">Concerns: {defaults.skin.skin_concerns.join(", ")}</div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Inspo Section - User's saved inspiration by category */}
                {inspirations.length > 0 && (
                  <>
                    <Separator className="my-4" />
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <Image className="w-4 h-4" /> Saved Inspo ({inspirations.length})
                      </h4>
                      
                      {/* Group inspo by category */}
                      {["hair", "nails", "face", "lashes", "makeup", "other"].map(category => {
                        const categoryInspo = inspirations.filter(e => 
                          (e.category || "other").toLowerCase() === category
                        );
                        if (categoryInspo.length === 0) return null;
                        
                        return (
                          <div key={category} className="mb-3">
                            <p className="text-xs font-medium text-gray-600 capitalize mb-2">{category} ({categoryInspo.length})</p>
                            <div className="grid grid-cols-4 gap-2">
                              {categoryInspo.slice(0, 8).map((inspo, idx) => (
                                <div key={idx} className="relative group aspect-square">
                                  {inspo.image_url || inspo.thumbnail ? (
                                    <img 
                                      src={inspo.image_url || inspo.thumbnail} 
                                      alt={inspo.name || "Inspo"}
                                      className="w-full h-full object-cover rounded-lg border"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Crect x='3' y='3' width='18' height='18' rx='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpath d='M21 15l-5-5L5 21'/%3E%3C/svg%3E";
                                      }}
                                    />
                                  ) : (
                                    <div className="w-full h-full rounded-lg bg-gray-100 flex items-center justify-center border">
                                      <Image className="w-4 h-4 text-gray-400" />
                                    </div>
                                  )}
                                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-end p-1">
                                    <span className="text-[9px] text-white truncate">{inspo.name || "Untitled"}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {categoryInspo.length > 8 && (
                              <p className="text-[10px] text-gray-400 mt-1">+{categoryInspo.length - 8} more</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <XCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No preferences saved</p>
                <p className="text-xs text-gray-400 mt-1">User hasn't completed any intake forms</p>
                
                {/* Still show inspo if they have it but no defaults */}
                {inspirations.length > 0 && (
                  <div className="mt-4 text-left">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <Image className="w-4 h-4" /> Saved Inspo ({inspirations.length})
                    </h4>
                    <div className="grid grid-cols-4 gap-2">
                      {inspirations.slice(0, 12).map((inspo, idx) => (
                        <div key={idx} className="relative group aspect-square">
                          {inspo.image_url || inspo.thumbnail ? (
                            <img 
                              src={inspo.image_url || inspo.thumbnail} 
                              alt={inspo.name || "Inspo"}
                              className="w-full h-full object-cover rounded-lg border"
                            />
                          ) : (
                            <div className="w-full h-full rounded-lg bg-gray-100 flex items-center justify-center border">
                              <Image className="w-4 h-4 text-gray-400" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Experiences Tab - grouped by pilot category */}
          <TabsContent value="events" className="p-4 space-y-4 m-0">
            {/* Preference Card Modal using AttendeePreferenceCard */}
            <AttendeePreferenceCardModal
              isOpen={!!viewingPreferenceCard}
              onClose={() => setViewingPreferenceCard(null)}
              event={{
                name: viewingPreferenceCard?.event_name || "Experience",
                event_id: viewingPreferenceCard?.event_id,
                cover_image: viewingPreferenceCard?.event_cover_image,
                date: viewingPreferenceCard?.event_date
              }}
              user={{
                name: user?.name,
                profile_photo_url: user?.profile_photo || user?.picture,
                wallet_id: user?.wallet_id
              }}
              preferences={{
                // Use user defaults (full preferences) instead of just RSVP data
                dietary: defaults?.dietary?.dietary_restrictions || defaults?.dietary_restrictions || viewingPreferenceCard?.dietary_restrictions || [],
                accessibility: { 
                  needs: defaults?.accessibility?.physical_needs || defaults?.accessibility_needs || viewingPreferenceCard?.accessibility_needs || [],
                  notes: defaults?.accessibility?.notes || defaults?.accessibility?.invisible_disabilities_notes
                },
                interests: defaults?.interests?.interests || defaults?.interests || viewingPreferenceCard?.interests || [],
                inspo: inspirations?.slice(0, 4) || viewingPreferenceCard?.linked_inspo || [],
                // Include additional preference categories
                hair: defaults?.hair,
                nails: defaults?.nails,
                face: defaults?.face,
                style: defaults?.style_options,
                vibe: defaults?.vibe_options
              }}
              labels={viewingPreferenceCard?.labels || []}
              isHost={viewingPreferenceCard?.is_host}
              isRsvp={true}
              showVenueContext={true}
            />
            
            {/* Group RSVPs by pilot category */}
            {rsvps.length > 0 ? (
              <div className="space-y-4">
                {/* Group by pilot type/category */}
                {["event", "instructor", "makeup", "hair", "nails", "lashes", "wellness", "culinary", "other"].map(category => {
                  const categoryRsvps = rsvps.filter(r => {
                    const eventType = (r.event_type || r.pilot_type || "event").toLowerCase();
                    if (category === "other") {
                      return !["event", "instructor", "makeup", "hair", "nails", "lashes", "wellness", "culinary"].includes(eventType);
                    }
                    return eventType.includes(category);
                  });
                  
                  if (categoryRsvps.length === 0) return null;
                  
                  return (
                    <div key={category}>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                        {category === "event" && <Calendar className="w-3 h-3" />}
                        {category === "instructor" && <Users className="w-3 h-3" />}
                        {category === "makeup" && <Sparkles className="w-3 h-3" />}
                        {category === "hair" && <Scissors className="w-3 h-3" />}
                        {category.charAt(0).toUpperCase() + category.slice(1)} Experiences ({categoryRsvps.length})
                      </h4>
                      <div className="space-y-2">
                        {categoryRsvps.map((rsvp, i) => (
                          <div key={i} className="p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{rsvp.event_name || rsvp.event_id}</span>
                              <div className="flex items-center gap-2">
                                {/* Eye icon to view preference card - always show */}
                                <button
                                  onClick={() => setViewingPreferenceCard(rsvp)}
                                  className="p-1 hover:bg-gray-200 rounded"
                                  title="View preference card"
                                >
                                  <Eye className="w-4 h-4 text-gray-500" />
                                </button>
                                <Badge variant={rsvp.status === "confirmed" ? "default" : "secondary"} className="text-xs">
                                  {rsvp.status}
                                </Badge>
                              </div>
                            </div>
                            {rsvp.event_date && (
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <Calendar className="w-3 h-3" />
                                {new Date(rsvp.event_date).toLocaleDateString()}
                              </div>
                            )}
                            {/* Compact preview of preferences */}
                            {(rsvp.dietary_restrictions?.length > 0 || rsvp.accessibility_needs?.length > 0) && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {rsvp.dietary_restrictions?.slice(0, 3).map((d, idx) => (
                                  <Badge key={`d-${idx}`} variant="outline" className="text-[10px] bg-green-50">{d}</Badge>
                                ))}
                                {rsvp.accessibility_needs?.slice(0, 2).map((a, idx) => (
                                  <Badge key={`a-${idx}`} variant="outline" className="text-[10px] bg-blue-50">{a}</Badge>
                                ))}
                                {((rsvp.dietary_restrictions?.length || 0) + (rsvp.accessibility_needs?.length || 0)) > 5 && (
                                  <Badge variant="outline" className="text-[10px]">+more</Badge>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Ticket className="w-6 h-6 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No experiences yet</p>
              </div>
            )}
          </TabsContent>

          {/* Linked Records Tab */}
          <TabsContent value="linked" className="p-4 space-y-4 m-0">
            {/* Inner Circle Memberships */}
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Star className="w-3 h-3" /> Inner Circle
              </h3>
              {innerCircleMemberships.length > 0 ? (
                <div className="space-y-2">
                  {innerCircleMemberships.map((m, i) => (
                    <div key={i} className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                      <div className="font-medium">{m.list_name || "Inner Circle"}</div>
                      <div className="text-xs text-gray-500">Source: {m.source || "Direct"}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">Not in any inner circle lists</p>
              )}
            </div>

            <Separator />

            {/* Organizations */}
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Building2 className="w-3 h-3" /> Organizations
              </h3>
              {orgs.length > 0 ? (
                <div className="space-y-2">
                  {orgs.map((org, i) => (
                    <div key={i} className="p-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                      <div className="font-medium">{org.name}</div>
                      <div className="text-xs text-gray-500">Role: {org.role || "Member"}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">Not in any organizations</p>
              )}
            </div>

            <Separator />

            {/* Hosting Pilots */}
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Hosting/Participating in Pilots
              </h3>
              {hostingPilots.length > 0 ? (
                <div className="space-y-2">
                  {hostingPilots.map((pilot, i) => (
                    <div key={i} className="p-2 bg-green-50 border border-green-200 rounded-lg text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{pilot.name || pilot.pilot_name}</span>
                        <Badge variant="outline" className="text-[10px] capitalize">
                          {pilot.type || pilot.pilot_type || "Event"} Pilot
                        </Badge>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {pilot.role && <span className="capitalize">Role: {pilot.role}</span>}
                        {pilot.role && pilot.status && " • "}
                        {pilot.status && <span>Status: {pilot.status}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Check RSVPs for pilot participation
                rsvps.length > 0 ? (
                  <div className="space-y-2">
                    {rsvps.filter(r => r.event_name).slice(0, 5).map((rsvp, i) => (
                      <div key={i} className="p-2 bg-green-50 border border-green-200 rounded-lg text-sm">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{rsvp.event_name}</span>
                          <Badge variant="outline" className="text-[10px] capitalize">
                            {rsvp.event_type || rsvp.pilot_type || "Event"} Pilot
                          </Badge>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Status: {rsvp.status || "Confirmed"}
                          {rsvp.checked_in_at && " • Checked In"}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Not participating in any pilots</p>
                )
              )}
            </div>

            <Separator />

            {/* Pilot Intakes */}
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Pilot Intakes
              </h3>
              {pilotIntakes.length > 0 ? (
                <div className="space-y-2">
                  {pilotIntakes.map((pilot, i) => (
                    <div key={i} className="p-2 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                      <div className="font-medium">{pilot.sub_category_name || pilot.pilot_type}</div>
                      <div className="text-xs text-gray-500">
                        Status: {pilot.status || "Pending"} 
                        {pilot.intake_completed && " • Intake Complete"}
                        {pilot.nda_signed && " • NDA Signed"}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No pilot intakes</p>
              )}
            </div>

            <Separator />

            {/* Beehiiv Sync */}
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Globe className="w-3 h-3" /> Beehiiv Newsletter
              </h3>
              {user.beehiiv_subscriber_id ? (
                <div className="p-2 bg-green-50 border border-green-200 rounded-lg text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-medium">Subscribed</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1 font-mono">{user.beehiiv_subscriber_id}</div>
                  {user.beehiiv_tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {user.beehiiv_tags.map((tag, idx) => (
                        <Badge key={idx} className="bg-orange-100 text-orange-700 text-[10px]">{tag}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400">Not synced to Beehiiv</p>
              )}
            </div>
          </TabsContent>

          {/* Edit Tab */}
          <TabsContent value="edit" className="p-4 space-y-4 m-0">
            <div className="space-y-3">
              {/* Basic Info - single canonical name field (Iter 9.39f) */}
              <div className="border-b pb-3 mb-3">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Basic Info</h4>
                <div>
                  <Label className="text-xs">Full Name</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Full name"
                    data-testid="user-edit-name"
                  />
                </div>
              </div>
              
              {/* Contact Info */}
              <div className="border-b pb-3 mb-3">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Contact</h4>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input
                      value={editForm.email}
                      onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="Email address"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input
                      value={editForm.phone}
                      onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="Phone number"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Location</Label>
                    <Input
                      value={editForm.location}
                      onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="City, State"
                    />
                  </div>
                </div>
              </div>
              
              {/* Business Info */}
              <div className="border-b pb-3 mb-3">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Business</h4>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Company</Label>
                    <Input
                      value={editForm.company}
                      onChange={(e) => setEditForm(prev => ({ ...prev, company: e.target.value }))}
                      placeholder="Company name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Title</Label>
                    <Input
                      value={editForm.title}
                      onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Job title"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Bio</Label>
                    <Textarea
                      value={editForm.bio}
                      onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                      placeholder="Short bio"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
              
              {/* Social Links */}
              <div className="border-b pb-3 mb-3">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Social</h4>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Instagram</Label>
                    <Input
                      value={editForm.instagram}
                      onChange={(e) => setEditForm(prev => ({ ...prev, instagram: e.target.value }))}
                      placeholder="@username"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">TikTok</Label>
                    <Input
                      value={editForm.tiktok}
                      onChange={(e) => setEditForm(prev => ({ ...prev, tiktok: e.target.value }))}
                      placeholder="@username"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">LinkedIn</Label>
                    <Input
                      value={editForm.linkedin}
                      onChange={(e) => setEditForm(prev => ({ ...prev, linkedin: e.target.value }))}
                      placeholder="LinkedIn URL or username"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Website</Label>
                    <Input
                      value={editForm.website}
                      onChange={(e) => setEditForm(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="Website URL"
                    />
                  </div>
                </div>
              </div>
              
              {/* Roles & Permissions - Multi-select role tags */}
              <div className="border-b pb-3 mb-3">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Roles (Multi-select)</h4>
                <div className="space-y-2">
                  <div className="relative">
                    <Label className="text-xs">User Roles - select all that apply</Label>
                    
                    {/* Selected Role Tags Display */}
                    <div className="flex flex-wrap gap-1 mb-2 min-h-[28px]">
                      {selectedRoleTags.length === 0 ? (
                        <span className="text-xs text-gray-400 italic">No roles assigned (default: customer)</span>
                      ) : (
                        selectedRoleTags.map((role, idx) => (
                          <Badge 
                            key={idx} 
                            className="text-xs flex items-center gap-1 pr-1 bg-purple-100 text-purple-700"
                          >
                            {role}
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedRoleTags(prev => prev.filter((_, i) => i !== idx));
                              }}
                              className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))
                      )}
                    </div>
                    
                    {/* Role Selector Dropdown */}
                    <div className="relative">
                      <Input
                        value={roleTagSearch}
                        onChange={(e) => {
                          setRoleTagSearch(e.target.value);
                          setShowRoleDropdown(true);
                        }}
                        onFocus={() => setShowRoleDropdown(true)}
                        placeholder="Search roles or click to see all..."
                        className="text-sm"
                        data-testid="role-tag-search"
                      />
                      
                      {showRoleDropdown && (
                        <div 
                          className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto"
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {ROLE_TAGS
                            .filter(role => 
                              role.toLowerCase().includes(roleTagSearch.toLowerCase()) &&
                              !selectedRoleTags.includes(role)
                            )
                            .map((role, idx) => (
                              <button
                                key={idx}
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-purple-50 flex items-center justify-between"
                                onClick={() => {
                                  setSelectedRoleTags(prev => [...prev, role]);
                                  setRoleTagSearch("");
                                  setShowRoleDropdown(false);
                                }}
                              >
                                <span className="capitalize">{role.replace(/_/g, " ")}</span>
                                <Plus className="w-3 h-3 text-purple-400" />
                              </button>
                            ))
                          }
                          <button
                            type="button"
                            className="w-full px-3 py-1 text-center text-xs text-gray-400 hover:bg-gray-50 border-t"
                            onClick={() => setShowRoleDropdown(false)}
                          >
                            Close
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Users can have multiple roles (e.g., host + instructor + admin)
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">Role Title (custom display title)</Label>
                    <Input
                      value={editForm.role_title}
                      onChange={(e) => setEditForm(prev => ({ ...prev, role_title: e.target.value }))}
                      placeholder="e.g., creative_director, founder"
                    />
                  </div>
                </div>
              </div>
              
              {/* Organizations - with Add functionality */}
              <div className="border-b pb-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase">Linked Organizations</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => setShowAddOrgDialog(true)}
                    data-testid="add-org-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add Org
                  </Button>
                </div>
                
                {/* Add Organization Dialog/Dropdown */}
                {showAddOrgDialog && (
                  <div className="mb-3 p-3 bg-gray-50 rounded-md border">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs font-medium">Search Organizations</Label>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddOrgDialog(false);
                          setOrgSearch("");
                          setOrgSearchResults([]);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <Input
                      value={orgSearch}
                      onChange={(e) => {
                        setOrgSearch(e.target.value);
                        searchOrganizations(e.target.value);
                      }}
                      placeholder="Type org name to search..."
                      className="text-sm mb-2"
                      data-testid="org-search-input"
                    />
                    {searchingOrgs && <p className="text-xs text-gray-400">Searching...</p>}
                    {orgSearchResults.length > 0 && (
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {orgSearchResults.map((org, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="w-full px-2 py-1.5 text-left text-sm hover:bg-blue-50 rounded flex items-center justify-between"
                            onClick={() => handleAddOrganization(org)}
                          >
                            <span>{org.name}</span>
                            <Plus className="w-3 h-3 text-blue-500" />
                          </button>
                        ))}
                      </div>
                    )}
                    {orgSearch.length >= 2 && orgSearchResults.length === 0 && !searchingOrgs && (
                      <p className="text-xs text-gray-400">No organizations found</p>
                    )}
                  </div>
                )}
                
                <div className="space-y-2">
                  {(editForm.organizations || []).length > 0 ? (
                    editForm.organizations.map((org, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-blue-50 rounded">
                        <div>
                          <span className="text-sm font-medium">{org.name}</span>
                          <span className="text-xs text-gray-500 ml-2">({org.role || org.user_role || "member"})</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-red-500"
                          onClick={() => handleRemoveOrganization(org.org_id, idx)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-400">No organizations linked</p>
                  )}
                </div>
              </div>
              
              {/* Quick Actions - Remove from all event teams */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Quick Actions</h4>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50 w-full"
                  data-testid="remove-from-all-teams-btn"
                  onClick={async () => {
                    if (!confirm(`Remove this user from ALL event teams (except events they own)?`)) return;
                    try {
                      const res = await fetch(`${API}/admin/users/${userId}/all-team-memberships`, {
                        method: "DELETE",
                        headers: getHeaders()
                      });
                      if (res.ok) {
                        const data = await res.json();
                        toast.success(`Removed from ${data.count} event team(s)`);
                      } else {
                        toast.error("Failed to remove from teams");
                      }
                    } catch (err) {
                      toast.error("Error removing from teams");
                    }
                  }}
                >
                  Remove from All Event Teams
                </Button>
              </div>
              
              {/* Tags & Notes */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Tags & Notes</h4>
                <div className="space-y-3">
                  {/* Searchable Tag Input */}
                  <div className="relative">
                    <Label className="text-xs">Tags (click to add, search existing)</Label>
                    
                    {/* Selected Tags Display */}
                    <div className="flex flex-wrap gap-1 mb-2 min-h-[28px]">
                      {selectedTags.map((tag, idx) => (
                        <Badge 
                          key={idx} 
                          variant="secondary" 
                          className="text-xs flex items-center gap-1 pr-1"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedTags(prev => prev.filter((_, i) => i !== idx));
                            }}
                            className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    
                    {/* Search Input */}
                    <div className="relative">
                      <Input
                        value={tagSearch}
                        onChange={(e) => {
                          setTagSearch(e.target.value);
                          setShowTagDropdown(true);
                        }}
                        onFocus={() => setShowTagDropdown(true)}
                        placeholder="Search or type new tag..."
                        className="text-sm"
                        data-testid="tag-search-input"
                      />
                      
                      {/* Tag Dropdown */}
                      {showTagDropdown && (
                        <div 
                          className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto"
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {/* Filtered available tags */}
                          {availableTags
                            .filter(tag => 
                              tag.toLowerCase().includes(tagSearch.toLowerCase()) &&
                              !selectedTags.includes(tag)
                            )
                            .slice(0, 15)
                            .map((tag, idx) => (
                              <button
                                key={idx}
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center justify-between"
                                onClick={() => {
                                  setSelectedTags(prev => [...prev, tag]);
                                  setTagSearch("");
                                  setShowTagDropdown(false);
                                }}
                              >
                                <span>{tag}</span>
                                <Plus className="w-3 h-3 text-gray-400" />
                              </button>
                            ))
                          }
                          
                          {/* Option to add custom tag */}
                          {tagSearch && !availableTags.includes(tagSearch) && !selectedTags.includes(tagSearch) && (
                            <button
                              type="button"
                              className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 text-blue-600 border-t flex items-center gap-2"
                              onClick={() => {
                                setSelectedTags(prev => [...prev, tagSearch.trim()]);
                                setTagSearch("");
                                setShowTagDropdown(false);
                              }}
                            >
                              <Plus className="w-3 h-3" />
                              Add "{tagSearch}" as new tag
                            </button>
                          )}
                          
                          {/* Close dropdown button */}
                          <button
                            type="button"
                            className="w-full px-3 py-1 text-center text-xs text-gray-400 hover:bg-gray-50 border-t"
                            onClick={() => setShowTagDropdown(false)}
                          >
                            Close
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-xs text-gray-400 mt-1">
                      {availableTags.length} tags available • Type to search or add new
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Admin Notes</Label>
                    <Textarea
                      value={editForm.notes}
                      onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Internal notes (not visible to user)"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-user-btn">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
            
            {/* Bottom padding to prevent cut-off */}
            <div className="h-16"></div>
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </div>
  );
}
